/**
 * Centralized Date Utilities
 * Provides consistent date handling across the entire system
 */

import {
  formatSafeDate,
  formatArabicDisplayDate,
  formatFrenchDisplayDate,
} from './formatSafety';

/**
 * Parse any date input (string, number, Date object) to ISO format (YYYY-MM-DD)
 * @param {any} input - Date input (string, number, Date object, Excel number)
 * @param {string} fallback - Fallback date if parsing fails (default: today)
 * @returns {string} ISO date string (YYYY-MM-DD) or fallback
 */
export const parseDate = (input, fallback = null) => {
  if (!input && input !== 0) {
    return fallback || getCurrentDate();
  }

  try {
    // Handle Excel numeric dates (days since 1900-01-01)
    if (typeof input === 'number') {
      // Excel date conversion: Excel stores dates as days since 1900-01-01
      // But Excel incorrectly treats 1900 as a leap year, so we need to adjust
      const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
      const excelDate = new Date(excelEpoch.getTime() + (input - 2) * 24 * 60 * 60 * 1000);
      
      if (!isNaN(excelDate.getTime())) {
        return excelDate.toISOString().split('T')[0];
      }
    }

    // Handle string dates
    if (typeof input === 'string') {
      const trimmedInput = input.trim();
      if (!trimmedInput) {
        return fallback || getCurrentDate();
      }

      // Try parsing the string
      const parsedDate = new Date(trimmedInput);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0];
      }
    }

    // Handle Date objects
    if (input instanceof Date) {
      if (!isNaN(input.getTime())) {
        return input.toISOString().split('T')[0];
      }
    }

    // If all parsing attempts fail, return fallback
    return fallback || getCurrentDate();
  } catch (error) {
    console.warn('Date parsing error:', error, 'Input:', input);
    return fallback || getCurrentDate();
  }
};

/**
 * Get current date in Mauritanian timezone (GMT+0) as ISO string
 * @returns {string} Current date in YYYY-MM-DD format
 */
export const getCurrentDate = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Get current datetime in Mauritanian timezone (GMT+0) as ISO string
 * @returns {string} Current datetime in ISO format
 */
export const getCurrentDateTime = () => {
  return new Date().toISOString();
};

/**
 * Format date for display in French locale
 * @param {string|Date} date - Date to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date, options = {}) => {
  if (!date) return '';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };

  return formatFrenchDisplayDate(date, defaultOptions);
};

/**
 * Format date for display in Arabic locale
 * @param {string|Date} date - Date to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDateForDisplayArabic = (date, options = {}) => {
  if (!date) return '';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };

  return formatArabicDisplayDate(date, defaultOptions);
};

/**
 * Validate if a date string is in correct ISO format (YYYY-MM-DD)
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid ISO date format
 */
export const isValidISODate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDateRegex.test(dateString)) {
    return false;
  }

  const date = new Date(dateString);
  return !isNaN(date.getTime()) && date.toISOString().split('T')[0] === dateString;
};

/**
 * Get date range for a month
 * @param {string} monthKey - Month key in format "YYYY-MM" or "current"
 * @returns {object} Object with startDate and endDate in ISO format
 */
export const getMonthDateRange = (monthKey) => {
  try {
    if (!monthKey || monthKey === 'current') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      if (isNaN(startOfMonth.getTime()) || isNaN(endOfMonth.getTime())) {
        console.error('Invalid current month dates');
        return { startDate: null, endDate: null };
      }
      
      return { 
        startDate: startOfMonth.toISOString().split('T')[0], 
        endDate: endOfMonth.toISOString().split('T')[0] 
      };
    }
    
    // Parse month key (format: "YYYY-MM" or "YYYY_MM")
    const [year, month] = monthKey.split(/[-_]/);
    
    if (!year || !month || isNaN(parseInt(year)) || isNaN(parseInt(month))) {
      console.error('Invalid month key format:', monthKey);
      return { startDate: null, endDate: null };
    }
    
    const yearNum = parseInt(year);
    const monthNum = parseInt(month);
    
    if (yearNum < 1900 || yearNum > 2100 || monthNum < 1 || monthNum > 12) {
      console.error('Invalid year or month range:', yearNum, monthNum);
      return { startDate: null, endDate: null };
    }
    
    const startOfMonth = new Date(yearNum, monthNum - 1, 1);
    const endOfMonth = new Date(yearNum, monthNum, 0);
    
    if (isNaN(startOfMonth.getTime()) || isNaN(endOfMonth.getTime())) {
      console.error('Invalid month dates for:', monthKey);
      return { startDate: null, endDate: null };
    }
    
    return { 
      startDate: startOfMonth.toISOString().split('T')[0], 
      endDate: endOfMonth.toISOString().split('T')[0] 
    };
  } catch (error) {
    console.error('Error in getMonthDateRange:', error, 'monthKey:', monthKey);
    return { startDate: null, endDate: null };
  }
};

/**
 * Add days to a date
 * @param {string|Date} date - Base date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date in ISO format (YYYY-MM-DD)
 */
export const addDaysToDate = (date, days) => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return getCurrentDate();
    }
    
    const newDate = new Date(dateObj);
    newDate.setDate(newDate.getDate() + days);
    
    return newDate.toISOString().split('T')[0];
  } catch (error) {
    console.warn('Error adding days to date:', error, 'Date:', date, 'Days:', days);
    return getCurrentDate();
  }
};

/**
 * Get tomorrow's date
 * @returns {string} Tomorrow's date in ISO format (YYYY-MM-DD)
 */
export const getTomorrowDate = () => {
  return addDaysToDate(getCurrentDate(), 1);
};

/**
 * Compare two dates
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1, date2) => {
  try {
    const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
    const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
    
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      return 0;
    }
    
    return d1.getTime() - d2.getTime();
  } catch (error) {
    console.warn('Error comparing dates:', error, 'Date1:', date1, 'Date2:', date2);
    return 0;
  }
};

/**
 * Check if a date is today
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  try {
    const dateStr = typeof date === 'string' ? date : parseDate(date);
    return dateStr === getCurrentDate();
  } catch (error) {
    console.warn('Error checking if date is today:', error, 'Date:', date);
    return false;
  }
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastDate = (date) => {
  try {
    const dateStr = typeof date === 'string' ? date : parseDate(date);
    return compareDates(dateStr, getCurrentDate()) < 0;
  } catch (error) {
    console.warn('Error checking if date is past:', error, 'Date:', date);
    return false;
  }
};

/**
 * Check if a date is in the future
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is in the future
 */
export const isFutureDate = (date) => {
  try {
    const dateStr = typeof date === 'string' ? date : parseDate(date);
    return compareDates(dateStr, getCurrentDate()) > 0;
  } catch (error) {
    console.warn('Error checking if date is future:', error, 'Date:', date);
    return false;
  }
};

/**
 * Format date for display in DD/MM/YYYY format
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string (DD/MM/YYYY)
 */
export const formatDateDDMMYYYY = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.warn('Error formatting date DD/MM/YYYY:', error, 'Date:', date);
    return '';
  }
};

/**
 * Format date and time for display in DD/MM/YYYY HH:MM:SS format
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time string (DD/MM/YYYY HH:MM:SS)
 */
export const formatDateTimeDDMMYYYY = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.warn('Error formatting date and time DD/MM/YYYY HH:MM:SS:', error, 'Date:', date);
    return '';
  }
};

/**
 * Format date for display in DD.MM.YYYY format
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string (DD.MM.YYYY)
 */
export const formatDateDDMMYYYYDots = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${day}.${month}.${year}`;
  } catch (error) {
    console.warn('Error formatting date DD.MM.YYYY:', error, 'Date:', date);
    return '';
  }
};

/**
 * Parse DD/MM/YYYY or DD.MM.YYYY format to ISO format
 * @param {string} dateString - Date string in DD/MM/YYYY or DD.MM.YYYY format
 * @returns {string} ISO date string (YYYY-MM-DD) or empty string if invalid
 */
export const parseDDMMYYYY = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return '';
  }

  try {
    // Handle both DD/MM/YYYY and DD.MM.YYYY formats
    const cleanDate = dateString.trim();
    const separator = cleanDate.includes('/') ? '/' : '.';
    const parts = cleanDate.split(separator);
    
    if (parts.length !== 3) {
      return '';
    }
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    
    // Validate ranges
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) {
      return '';
    }
    
    // Create date object (month is 0-indexed in JavaScript)
    const dateObj = new Date(year, month - 1, day);
    
    // Validate the date (handles invalid dates like 31/02/2024)
    if (dateObj.getDate() !== day || dateObj.getMonth() !== month - 1 || dateObj.getFullYear() !== year) {
      return '';
    }
    
    return dateObj.toISOString().split('T')[0];
  } catch (error) {
    console.warn('Error parsing DD/MM/YYYY date:', error, 'Date:', dateString);
    return '';
  }
};

/**
 * Format date for display in localized DD/MM/YYYY format
 * @param {string|Date} date - Date to format
 * @param {string} locale - Locale ('fr', 'ar', 'en')
 * @returns {string} Formatted date string
 */
export const formatDateLocalizedDDMMYYYY = (date, locale = 'fr') => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    
    // Use different separators based on locale
    const separator = locale === 'ar' ? '/' : (locale === 'en' ? '/' : '/');
    
    return `${day}${separator}${month}${separator}${year}`;
  } catch (error) {
    console.warn('Error formatting localized DD/MM/YYYY date:', error, 'Date:', date, 'Locale:', locale);
    return '';
  }
};

/**
 * Get current date in DD/MM/YYYY format
 * @returns {string} Current date in DD/MM/YYYY format
 */
export const getCurrentDateDDMMYYYY = () => {
  return formatDateDDMMYYYY(getCurrentDate());
};

/**
 * Get current date in DD.MM.YYYY format
 * @returns {string} Current date in DD.MM.YYYY format
 */
export const getCurrentDateDDMMYYYYDots = () => {
  return formatDateDDMMYYYYDots(getCurrentDate());
};
